/** @format */

import Close from '@/assets/joivy_close_miniature.svg';
import { FormattedMessage } from 'gatsby-plugin-intl';
import React, { ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';

interface DialogProps {
  title: string;
  children: ReactNode;
  setModalOpen: (value: boolean) => void;
}

const ModalDialog: React.FC<DialogProps> = ({ title, children, setModalOpen }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: any) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setModalOpen(false);
    }
  };

  useEffect(() => {
    // Add the event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Remove the event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <ModalDialogWrapper ref={modalRef}>
      <ModalHeader>
        <div />
        <h2>
          <FormattedMessage id={title} />
        </h2>
        <CloseButton onClick={() => setModalOpen(false)}>
          <img src={Close} alt="Close" width="28" />
        </CloseButton>
      </ModalHeader>
      <ModalContent>{children}</ModalContent>
    </ModalDialogWrapper>
  );
};

export default ModalDialog;

const ModalContent = styled.div`
  margin-bottom: 1rem;
  display: block;
  position: relative;
  height: calc(100% - var(--modal-header-height));
  width: 100%;
  padding: 1.25rem;

  h3:first-child {
    margin-top: 0;
  }
`;

const CloseButton = styled.button`
  cursor: pointer;
  background-color: transparent;
  display: flex;
  align-items: center;
  border: 0;
  padding: 0;

  img {
    filter: brightness(0) invert(1);
  }
`;

const ModalHeader = styled.div`
  height: var(--modal-header-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.joivy.pink};
  padding-inline: 1.25rem;

  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    padding: 0.75rem;
    > div {
      display: none;
    }
  }
  h2 {
    color: ${({ theme }) => theme.colors.joivy.white};
    text-align: center;
    font-size: clamp(1.125rem, 1.0417rem + 0.3704vw, 1.375rem);
    letter-spacing: clamp(0.0225rem, 0.0208rem + 0.0074vw, 0.0275rem);
    margin-block: 0;
  }
`;

const ModalDialogWrapper = styled.div`
  --modal-header-height: 3.75rem;

  max-width: 780px;
  margin: 0 auto;
  height: 100%;
  max-height: 684px;
  border-radius: 1.875rem;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.joivy.white};
  margin: 2.5rem auto;

  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    margin: 0;
    border-radius: 0;
    max-height: 100%;
  }
`;
